import React from 'react';
import * as Sentry from '@sentry/react';

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary so an uncaught error in any view doesn't
 * unmount the dashboard tree and leave the instructor on a blank page.
 * We log to console for now; a Sentry hook can replace the logger later.
 */
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught:', error, info);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 24,
            gap: 16,
            fontFamily: 'system-ui, sans-serif',
            background: '#0f172a',
            color: '#e2e8f0',
            textAlign: 'center',
          }}
        >
          <h1 style={{ margin: 0, fontSize: 24 }}>Something went wrong</h1>
          <p style={{ margin: 0, maxWidth: 520, color: '#94a3b8' }}>
            The dashboard hit an unexpected error and stopped. Try reloading, or contact platform
            support if it keeps happening.
          </p>
          <details style={{ maxWidth: 520, color: '#94a3b8', fontSize: 12 }}>
            <summary style={{ cursor: 'pointer' }}>Technical details</summary>
            <pre style={{ textAlign: 'left', whiteSpace: 'pre-wrap', marginTop: 8 }}>
              {this.state.error.message}
            </pre>
          </details>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 18px',
                background: 'transparent',
                color: '#e2e8f0',
                border: '1px solid #475569',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
